import { useTranslation } from 'react-i18next';

export interface AccessExpiryValue {
  accessDurationDays?: number;
  accessExpiresAt?: string; // YYYY-MM-DD
}

interface AccessExpiryConfigProps {
  value: AccessExpiryValue;
  /** Always emits both fields, with the inactive one cleared (so saves stay mutually exclusive). */
  onChange: (next: { accessDurationDays: number; accessExpiresAt: string }) => void;
}

type Mode = 'never' | 'duration' | 'fixed';

function modeOf(value: AccessExpiryValue): Mode {
  if (value.accessExpiresAt && value.accessExpiresAt.trim() !== '') return 'fixed';
  if (value.accessDurationDays && Number(value.accessDurationDays) > 0) return 'duration';
  return 'never';
}

/**
 * Configure default access expiration for a course or exam. The student's personal
 * expiry is computed from this at enrollment / first exam access and can be extended
 * individually later. Duration and fixed-date are mutually exclusive.
 */
export default function AccessExpiryConfig({ value, onChange }: AccessExpiryConfigProps) {
  const { t } = useTranslation('common');
  const mode = modeOf(value);
  const days = value.accessDurationDays && Number(value.accessDurationDays) > 0 ? Number(value.accessDurationDays) : 365;

  const selectMode = (next: Mode) => {
    if (next === 'never') onChange({ accessDurationDays: 0, accessExpiresAt: '' });
    else if (next === 'duration') onChange({ accessDurationDays: days, accessExpiresAt: '' });
    else onChange({ accessDurationDays: 0, accessExpiresAt: value.accessExpiresAt || '' });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-600">{t('expiry.config.title')}</label>
        <p className="text-xs text-gray-400">{t('expiry.config.help')}</p>
      </div>

      <div className="space-y-2">
        {(['never', 'duration', 'fixed'] as const).map((m) => (
          <label key={m} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="access-expiry-mode"
              checked={mode === m}
              onChange={() => selectMode(m)}
              className="mt-1"
            />
            <span className="text-sm text-gray-700">
              {t(`expiry.config.mode.${m}`)}
              <span className="block text-xs text-gray-400">{t(`expiry.config.modeHint.${m}`)}</span>
            </span>
          </label>
        ))}
      </div>

      {mode === 'duration' && (
        <div className="pl-6">
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('expiry.config.durationLabel')}</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => onChange({ accessDurationDays: Math.max(1, Number(e.target.value) || 0), accessExpiresAt: '' })}
              className="w-32 px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-500">{t('expiry.config.daysSuffix')}</span>
          </div>
        </div>
      )}

      {mode === 'fixed' && (
        <div className="pl-6">
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('expiry.config.fixedLabel')}</label>
          <input
            type="date"
            value={value.accessExpiresAt || ''}
            onChange={(e) => onChange({ accessDurationDays: 0, accessExpiresAt: e.target.value })}
            className="w-48 px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}
    </div>
  );
}
